import {
  OktonLoadingCard,
  OktonCheckbox,
  OktonAlert,
  OktonEmptyState,
  OktonErrorCard,
  OktonPageHeader,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, Coins } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  useContasBancarias,
  useMoedasVinculaveis,
  useSaveVinculos,
  useVinculosDaConta,
} from '../../../services/hooks/useContasBancarias';

/**
 * Escolhe as Moedas que liquidam numa Conta Bancária.
 *
 * Só Moeda do tipo Banco: é ela que representa dinheiro parado em conta. Uma
 * Conta tem várias — "BANCO SICOOB", "PIX SICOOB", "CRÉDITO SICOOB" —, todas
 * de Banco, e é o nome que distingue por onde o dinheiro entrou.
 *
 * A do cadastro da Conta aparece marcada como principal e não entra na
 * escolha: é ela que Saque, Depósito e Transferência usam.
 *
 * A lista de disponíveis só traz Moeda que ainda não está em nenhuma Conta —
 * uma Moeda liquida numa Conta só — e que seja do mesmo Banco da Conta: uma
 * Moeda do Sicoob numa conta do Bradesco deixaria extrato e financeiro em
 * bancos diferentes, sem nada acusando.
 */
const MoedasDaConta = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const { idConta } = useParams<{ idConta: string }>();

  const { data: contas = [] } = useContasBancarias();
  const {
    data: vinculos = [],
    isLoading,
    isError,
    refetch,
  } = useVinculosDaConta(idConta);
  const { data: vinculaveis = [] } = useMoedasVinculaveis(idConta);
  const saveMutation = useSaveVinculos();

  const [marcadas, setMarcadas] = useState<string[]>([]);
  const [erro, setErro] = useState('');

  const conta = contas.find(c => c.id_cts === idConta);
  const principal = vinculos.find(v => v.principal_ctm === '1');

  // O que já está vinculado a esta Conta continua na lista de escolha; o que
  // está em outra Conta não aparece para ninguém.
  const opcoes = useMemo(() => {
    const daConta = vinculos
      .filter(v => v.principal_ctm !== '1')
      .map(v => ({
        id_moe: v.moeda_ctm,
        nome_moe: v.nome_moe,
        nome_tpt: v.nome_tpt,
      }));
    const livres = vinculaveis.map(m => ({
      id_moe: m.id_moe,
      nome_moe: m.nome_moe,
      nome_tpt: m.nome_tpt,
    }));
    return [...daConta, ...livres].sort((a, b) =>
      a.nome_moe.localeCompare(b.nome_moe),
    );
  }, [vinculos, vinculaveis]);

  // Os vínculos chegam depois do primeiro render: sem isso a tela abriria com
  // nada marcado e salvar desvincularia tudo.
  useEffect(() => {
    setMarcadas(
      vinculos.filter(v => v.principal_ctm !== '1').map(v => v.moeda_ctm),
    );
  }, [vinculos]);

  const alternar = (idMoeda: string) => {
    setMarcadas(atual =>
      atual.includes(idMoeda)
        ? atual.filter(m => m !== idMoeda)
        : [...atual, idMoeda],
    );
  };

  const salvar = async () => {
    setErro('');
    try {
      await saveMutation.mutateAsync({ conta: idConta, moedas: marcadas });
      history.push('/contas-bancarias');
    } catch (err) {
      setErro(
        err instanceof Error ? err.message : 'Erro ao gravar os vínculos.',
      );
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Moedas da Conta"
        subtitle={
          conta
            ? `Formas de pagamento que liquidam em ${conta.descricao_cts}`
            : 'Formas de pagamento que liquidam nesta Conta'
        }
        onBack={() => history.push('/contas-bancarias')}
        actions={
          <>
            {!!conta?.id_ban && (
              <Flex ml="auto" my="3" align="center">
                <Flex
                  borderWidth="1px"
                  p="2"
                  rounded="2xl"
                  bg={bg}
                  align="center"
                >
                  <Button
                    leftIcon={<Coins size={18} />}
                    variant="outline"
                    colorScheme="blue"
                    borderWidth="2px"
                    onClick={() =>
                      history.push(`/moedas?banco=${conta.id_ban}`)
                    }
                  >
                    Moedas de {conta.nome_ban ?? 'este banco'}
                  </Button>
                </Flex>
              </Flex>
            )}
          </>
        }
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {!!erro && <OktonAlert status="error" description={erro} />}

          {!!principal && (
            <Box bg={subtleBg} borderRadius="lg" px="4" py="3">
              <Text fontSize="xs" color="gray.500">
                Moeda do banco
              </Text>
              <Flex align="center" gridGap="2" mt="1">
                <Text fontWeight="semibold" color={color}>
                  {principal.nome_moe}
                </Text>
                <Badge colorScheme="blue" borderRadius="full" px="2">
                  {principal.nome_tpt ?? 'Banco'}
                </Badge>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt="1">
                Vem do cadastro da Conta e é a usada por Saque, Depósito e
                Transferência. Não é alterada aqui.
              </Text>
            </Box>
          )}

          {isLoading ? (
            <OktonLoadingCard h="200px" />
          ) : isError ? (
            <OktonErrorCard h="200px" onRetry={refetch} />
          ) : (
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb="2">
                Outras Moedas que liquidam nesta Conta
              </Text>
              {opcoes.length === 0 ? (
                <Box borderWidth="1px" borderRadius="lg">
                  <OktonEmptyState
                    icon={Coins}
                    title="Nenhuma moeda disponível"
                    description="Cadastre uma Moeda de Banco com este mesmo Banco, ou desvincule de outra Conta."
                    action={
                      !!conta?.id_ban && (
                        <Button
                          size="sm"
                          mt="1"
                          variant="outline"
                          colorScheme="blue"
                          borderWidth="2px"
                          leftIcon={<Coins size={16} />}
                          onClick={() =>
                            history.push(`/moedas?banco=${conta.id_ban}`)
                          }
                        >
                          Ir para as Moedas deste Banco
                        </Button>
                      )
                    }
                  />
                </Box>
              ) : (
                <Stack
                  spacing="0"
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                >
                  {opcoes.map(opcao => (
                    <Flex
                      key={opcao.id_moe}
                      align="center"
                      gridGap="3"
                      px="4"
                      py="3"
                      borderBottomWidth="1px"
                      _last={{ borderBottomWidth: 0 }}
                      _hover={{ bg: subtleBg }}
                    >
                      <Box flexShrink={0}>
                        <OktonCheckbox
                          name={`moedas-conta-${opcao.id_moe}`}
                          label={null}
                          size="md"
                          isChecked={marcadas.includes(opcao.id_moe)}
                          onChange={() => alternar(opcao.id_moe)}
                        />
                      </Box>
                      <Box flex="1" minW="0">
                        <Text
                          as="label"
                          htmlFor={`moedas-conta-${opcao.id_moe}`}
                          display="block"
                          fontSize="sm"
                          fontWeight="medium"
                          isTruncated
                        >
                          {opcao.nome_moe}
                        </Text>
                      </Box>
                      <Badge borderRadius="full" px="2" colorScheme="gray">
                        {opcao.nome_tpt ?? '—'}
                      </Badge>
                    </Flex>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          <Text fontSize="xs" color="gray.500">
            Só Moeda do tipo Banco e do mesmo Banco da Conta pode ser vinculada,
            e cada uma liquida numa Conta só — a que já estiver em outra não
            aparece aqui.
          </Text>

          <Flex gridGap="3" pt="2">
            <Button
              variant="ghost"
              onClick={() => history.push('/contas-bancarias')}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                saveMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={saveMutation.isLoading || isLoading}
              onClick={salvar}
            >
              Salvar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default MoedasDaConta;
